import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  checkDraftStatusApiV1ReportGeneratorTemplatesIdCasesCaseNumberDraftStatusGet,
  getAllTemplatesApiV1ReportGeneratorTemplatesGet,
  getDraftReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberDraftReportGet,
  getResponseByIdApiV1ReportGeneratorResponsesResponseIdGet,
  getTemplateApiV1ReportGeneratorTemplatesIdGet,
} from "../../api/generated/sdk.gen";
import {
  ReportGeneratorTemplateSchema,
  ReportGeneratorTemplateWithTypeSchema,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { docxBufferIsMissingMedia } from "../../utils/docx-media-check";

// The templates endpoint is paginated (default page size 10). This hook is
// used to populate pick-one dropdowns (e.g. Create Case), which need the FULL
// list, not just page 1 — otherwise the newest templates fill page 1 and older
// ones silently disappear from the picker (MDRS-118: the 11 seeded service-type
// templates were hiding the original 4 + General). We request the endpoint's
// max page size so the whole set comes back in one call.
//
// TODO (follow-up): the system-template count is small and bounded today, but
// custom templates can grow unbounded. If this list gets large, switch the
// picker to lazy loading (paged fetch-on-scroll / search-server-side) instead
// of pulling all 100 up front. Tracked as a candidate for the ReportTemplates
// management screen too.
const MAX_TEMPLATES_PAGE_SIZE = 100;

/**
 * Fetch all available templates from the API
 * @param enabled - Whether the query should run
 * @param sort_by - Field to sort by ("created_at" or "order")
 * @param sort_order - Sort direction ("asc" or "desc")
 * @returns Query result with templates data
 */
export const useTemplates = ({
  enabled = true,
  sort_by = "created_at",
  sort_order = "desc",
}: {
  enabled?: boolean;
  sort_by?: "created_at" | "order";
  sort_order?: "asc" | "desc";
} = {}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.REPORT_GENERATOR,
      CLIENT_KEYS.TEMPLATES,
      sort_by,
      sort_order,
    ],
    queryFn: async (): Promise<ReportGeneratorTemplateWithTypeSchema[]> => {
      const response = await getAllTemplatesApiV1ReportGeneratorTemplatesGet({
        client: apiClient,
        query: {
          sort_by,
          sort_order,
          limit: MAX_TEMPLATES_PAGE_SIZE,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch templates: ${JSON.stringify(response.error)}`
        );
      }

      // Extract items from paginated response
      return (response.data?.items || []) as ReportGeneratorTemplateWithTypeSchema[];
    },
    enabled,
  });
};

/**
 * Fetch a specific template by ID from the API
 * @param templateId - The ID of the template to fetch
 * @param enabled - Whether the query should run
 * @returns Query result with template data
 */
export const useTemplateById = (
  templateId?: string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.TEMPLATES, templateId],
    queryFn: async (): Promise<ReportGeneratorTemplateSchema> => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      const response = await getTemplateApiV1ReportGeneratorTemplatesIdGet({
        client: apiClient,
        path: { id: templateId },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch template: ${JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        throw new Error(`Template with ID ${templateId} not found`);
      }

      return response.data;
    },
    enabled: enabled && !!templateId,
  });
};

/**
 * Fetch the template's source .docx as an ArrayBuffer.
 *
 * Two-step: backend mints a short-lived SAS URL for the template_url blob,
 * then we GET the bytes from Azure Blob. The SAS is 1h-lived; we cache the
 * buffer for 30min so a long editing session doesn't churn it. The
 * underlying generated SDK method does not exist yet (regen the API client
 * to pick up GET /templates/{id}/raw-docx-url); we call apiClient directly.
 */
export const useTemplateRawDocxBuffer = (
  templateId?: string,
  {
    enabled = true,
    /**
     * Pre-signed URL of a user-edited WIP draft .docx. When set, the
     * editor loads this instead of the bare template — restoring the
     * user's in-progress edits on refresh. Keyed into the queryKey so
     * a freshly saved draft invalidates the cached template buffer.
     */
    draftDocxDownloadUrl,
  }: { enabled?: boolean; draftDocxDownloadUrl?: string | null } = {}
) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.REPORT_GENERATOR,
      CLIENT_KEYS.TEMPLATES,
      templateId,
      "raw-docx-buffer",
      draftDocxDownloadUrl ? "draft" : "template",
    ],
    queryFn: async (): Promise<ArrayBuffer> => {
      if (!templateId) throw new Error("Template ID is required");

      const fetchTemplateBuffer = async (): Promise<ArrayBuffer> => {
        const urlResp = await apiClient.get<
          { 200: { download_url: string } },
          { 422: { detail?: string } }
        >({
          url: `/api/v1/report-generator/templates/${templateId}/raw-docx-url`,
          security: [{ scheme: "bearer", type: "http" }],
        });

        if ("error" in urlResp && urlResp.error) {
          throw new Error(
            `Failed to mint template SAS URL: ${JSON.stringify(urlResp.error)}`
          );
        }
        const downloadUrl = urlResp.data?.download_url;
        if (!downloadUrl) throw new Error("Template has no .docx configured");

        const blobResp = await fetch(downloadUrl);
        if (!blobResp.ok) {
          throw new Error(
            `Failed to download template blob: ${blobResp.status} ${blobResp.statusText}`
          );
        }
        return await blobResp.arrayBuffer();
      };

      // Prefer the user's WIP draft .docx when present; falls back to
      // the bare template if no draft has been saved yet.
      if (draftDocxDownloadUrl) {
        const blobResp = await fetch(draftDocxDownloadUrl);
        if (!blobResp.ok) {
          throw new Error(
            `Failed to download draft blob: ${blobResp.status} ${blobResp.statusText}`
          );
        }
        const draftBuffer = await blobResp.arrayBuffer();
        // Draft blobs saved before the autosave media-guard landed can be
        // in a known-broken state: <w:drawing> references survive but the
        // word/media binaries are gone, so the editor renders empty <img>
        // elements for signatures / letterhead. When we detect that, drop
        // the corrupt draft and load the original template so the user
        // sees their images again. Module choices still apply on top via
        // draftData (handled by ReportBuilderEditorProvider).
        if (await docxBufferIsMissingMedia(draftBuffer)) {
          console.warn(
            "[buffer] saved draft is missing media binaries; falling back to template"
          );
          return await fetchTemplateBuffer();
        }
        return draftBuffer;
      }

      return await fetchTemplateBuffer();
    },
    enabled: enabled && !!templateId,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

/**
 * Check if there's a draft report for a specific template and case number
 * @param templateId - The ID of the template
 * @param caseNumber - The case number
 * @param enabled - Whether the query should run
 * @returns Query result with draft status data including has_draft and draft_data if available
 */
export const useCheckDraftStatus = ({
  templateId,
  caseNumber,
  enabled = true,
}: {
  templateId: string;
  caseNumber: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.REPORT_GENERATOR,
      CLIENT_KEYS.TEMPLATES,
      templateId,
      CLIENT_KEYS.CASES,
      CLIENT_KEYS.DRAFT_STATUS,
      caseNumber,
    ],
    queryFn: async () => {
      if (!templateId || !caseNumber) {
        throw new Error("Template ID and case number are required");
      }

      const response =
        await checkDraftStatusApiV1ReportGeneratorTemplatesIdCasesCaseNumberDraftStatusGet(
          {
            client: apiClient,
            path: {
              id: templateId,
              case_number: caseNumber,
            },
          }
        );

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to check draft status: ${JSON.stringify(response.error)}`
        );
      }

      // Ensure we always return the expected shape
      const data = response.data || {};
      return {
        has_draft_report: !!(data as any).has_draft_report,
        has_draft: !!(data as any).has_draft,
        draft_id: (data as any).draft_id || null,
        created_at: (data as any).created_at || null,
        updated_at: (data as any).updated_at || null,
        draft_data: (data as any).draft_data || {},
        // WIP .docx state for SplitDocx — `draft_docx_download_url` is
        // a short-lived SAS URL the editor fetches on mount. Points at
        // the editor-view (header-flattened copy) so the SplitDocx
        // canvas can render without overlap.
        draft_docx_url: (data as any).draft_docx_url || null,
        draft_docx_saved_at: (data as any).draft_docx_saved_at || null,
        draft_docx_download_url: (data as any).draft_docx_download_url || null,
        // MDRS-98: documents schedule derived from the case's files, surfaced
        // regardless of whether the template declares a docs module. The editor's
        // documentsSource falls back to this. Must be forwarded here — this hook
        // reshapes the response into a fixed object, so unlisted keys are dropped.
        documents_summary: (data as any).documents_summary ?? null,
        // MDRS-94: template-independent LOI questions, same reshaping caveat as
        // documents_summary above. The editor's qtaSource falls back to this;
        // if it isn't forwarded, the QtA source is always empty and the
        // "Responses to Specific Questions" heading renders as a blank stub even
        // when the backend returned the questions.
        list_of_questions: (data as any).list_of_questions ?? null,
      };
    },
    enabled: enabled && !!templateId && !!caseNumber,
  });
};

/**
 * Get draft report for a specific template and case number
 * @param templateId - The ID of the template
 * @param caseNumber - The case number
 * @param enabled - Whether the query should run
 * @returns Query result with full draft report data
 */
export const useGetDraftReport = ({
  templateId,
  caseNumber,
  enabled = true,
}: {
  templateId: string;
  caseNumber: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.REPORT_GENERATOR,
      CLIENT_KEYS.TEMPLATES,
      templateId,
      CLIENT_KEYS.CASES,
      "draft-report",
      caseNumber,
    ],
    queryFn: async () => {
      if (!templateId || !caseNumber) {
        throw new Error("Template ID and case number are required");
      }

      const response =
        await getDraftReportApiV1ReportGeneratorTemplatesIdCasesCaseNumberDraftReportGet(
          {
            client: apiClient,
            path: {
              id: templateId,
              case_number: caseNumber,
            },
          }
        );

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to get draft report: ${JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    enabled: enabled && !!templateId && !!caseNumber,
    retry: false, // Don't retry on 404
  });
};

/**
 * Get response by ID
 * @param responseId - The ID of the response
 * @param enabled - Whether the query should run
 * @returns Query result with full response data
 */
export const useGetResponseById = ({
  responseId,
  enabled = true,
}: {
  responseId?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.REPORT_GENERATOR, CLIENT_KEYS.RESPONSES, responseId],
    queryFn: async () => {
      if (!responseId) {
        throw new Error("Response ID is required");
      }

      const response =
        await getResponseByIdApiV1ReportGeneratorResponsesResponseIdGet({
          client: apiClient,
          path: {
            response_id: responseId,
          },
        });

      if ("error" in response && response.error) {
        throw new Error(`Failed to get response: ${JSON.stringify(response.error)}`);
      }

      return response.data;
    },
    enabled: enabled && !!responseId,
    retry: false, // Don't retry on 404
  });
};

/**
 * Fetch source content (extract snippets + summaries) for the middle column.
 *
 * Returns an array of section bundles, each containing per-module
 * `extracts` (individual snippet cards) plus optional merged
 * `doc_sorter_summary` / `assessment_scribe_summary` blobs the UI
 * surfaces as whole-paragraph "Summary from X" cards.
 */
export type SourceExtract = {
  id: string;
  text: string;
  source_file_name?: string | null;
  page_number?: number | null;
  category_slug?: string | null;
  extract_type: string;
  target_field: string;
  order: number;
  // Flat cross-module manual sort position for the All-Notes view (MDRS-8);
  // null/absent until the user hand-sorts.
  global_order?: number | null;
};

export type ModuleSourceSnippets = {
  module_name: string;
  extracts: SourceExtract[];
  doc_sorter_summary?: string | null;
  assessment_scribe_summary?: string | null;
};

export type SectionSourceSnippets = {
  section_name: string;
  modules: Record<string, ModuleSourceSnippets>;
};

export const useSourceSnippets = ({
  templateId,
  caseNumber,
  enabled = true,
}: {
  templateId: string;
  caseNumber: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.REPORT_GENERATOR,
      CLIENT_KEYS.TEMPLATES,
      templateId,
      CLIENT_KEYS.CASES,
      caseNumber,
      "source-snippets",
    ],
    queryFn: async (): Promise<SectionSourceSnippets[]> => {
      // Relative path — apiClient is preconfigured with baseUrl. (Manually
      // prepending VITE_BACKEND_URL double-prefixed the URL and broke the
      // fetch, so notes never loaded.)
      const url = `/api/v1/report-generator/templates/${encodeURIComponent(
        templateId
      )}/cases/${encodeURIComponent(caseNumber)}/source-snippets`;
      const result = await apiClient.get<SectionSourceSnippets[]>({ url });
      if (result.error) {
        throw new Error(
          `Failed to load source snippets: ${JSON.stringify(result.error)}`
        );
      }
      return (result.data as SectionSourceSnippets[] | undefined) ?? [];
    },
    enabled: enabled && !!templateId && !!caseNumber,
  });
};
